import React, { useState, useEffect } from 'react'
import { Modal, Form, Input, Button, Avatar, Typography, Upload, List, Tag, Space, message, Spin } from 'antd'
import {
  SendOutlined,
  PictureOutlined,
  FileOutlined,
  DeleteOutlined,
  UserOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import { useTaskComments } from '../../hooks/useTaskComments'
import { useAuth } from '../../contexts/AuthContext'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/th'

dayjs.extend(relativeTime)
dayjs.locale('th')

const { TextArea } = Input
const { Text, Paragraph } = Typography

const TaskCommentsModal = ({ visible, task, onClose }) => {
  const [form] = Form.useForm()
  const { user } = useAuth()
  const [submittingComment, setSubmittingComment] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [replyTo, setReplyTo] = useState(null)

  const {
    comments,
    attachments,
    loading,
    error,
    createComment,
    uploadAttachment,
    deleteComment,
    canCreateComments = true,
    canDeleteComments = true
  } = useTaskComments(task?.id, { autoFetch: visible })

  // รีเซ็ตฟอร์มเมื่อเปิด/ปิดโมดัล
  useEffect(() => {
    if (visible) {
      form.resetFields()
      setReplyTo(null)
    }
  }, [visible, form])

  // จัดกลุ่มความเห็นแบบ hierarchy
  const organizeComments = (comments) => {
    const commentMap = {}
    const rootComments = []

    // สร้าง map และเพิ่ม replies array
    comments.forEach(comment => {
      comment.replies = []
      commentMap[comment.id] = comment
    })

    // จัดเรียงความเห็น
    comments.forEach(comment => {
      if (comment.parent_comment_id) {
        const parent = commentMap[comment.parent_comment_id]
        if (parent) {
          parent.replies.push(comment)
        }
      } else {
        rootComments.push(comment)
      }
    })

    // เรียงตามเวลาสร้าง
    rootComments.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    rootComments.forEach(comment => {
      comment.replies.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    })

    return rootComments
  }

  const organizedComments = organizeComments(comments)

  // สร้างความเห็นใหม่
  const handleSubmitComment = async (values) => {
    if (!values.comment_text.trim()) {
      message.error('กรุณาใส่เนื้อหาความเห็น')
      return
    }

    try {
      setSubmittingComment(true)

      const commentData = {
        comment_text: values.comment_text,
        parent_comment_id: replyTo,
        mentions: values.mentions || null,
        is_internal: values.is_internal || false,
        is_resolved: values.is_resolved || false
      }

      const result = await createComment(commentData)

      if (result.success) {
        form.resetFields()
        setReplyTo(null)
        message.success('สร้างความเห็นสำเร็จ')
      } else {
        message.error(result.message || 'ไม่สามารถสร้างความเห็นได้')
      }
    } catch (error) {
      console.error('Error creating comment:', error)
      message.error('เกิดข้อผิดพลาดในการสร้างความเห็น')
    } finally {
      setSubmittingComment(false)
    }
  }

  // อัปโหลดไฟล์แนบ
  const handleUpload = async (file) => {
    try {
      setUploadingFile(true)

      const result = await uploadAttachment(file, {
        description: `ไฟล์แนบจากความเห็น`
      })

      if (result.success) {
        message.success('อัปโหลดไฟล์สำเร็จ')
      } else {
        message.error(result.message || 'ไม่สามารถอัปโหลดไฟล์ได้')
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      message.error('เกิดข้อผิดพลาดในการอัปโหลดไฟล์')
    } finally {
      setUploadingFile(false)
    }
  }

  // ลบความเห็น
  const handleDeleteComment = async (comment) => {
    try {
      const result = await deleteComment(comment.id)
      if (result.success) {
        message.success('ลบความเห็นสำเร็จ')
      } else {
        message.error(result.message || 'ไม่สามารถลบความเห็นได้')
      }
    } catch (error) {
      console.error('Error deleting comment:', error)
      message.error('เกิดข้อผิดพลาดในการลบความเห็น')
    }
  }

  // เริ่มตอบกลับความเห็น
  const handleReply = (comment) => {
    setReplyTo(comment.id)
    form.setFieldsValue({
      comment_text: `@${comment.user_name} `
    })
  }

  // ยกเลิกการตอบกลับ
  const handleCancelReply = () => {
    setReplyTo(null)
    form.resetFields()
  }

  // รูปแบบไฟล์แนบ
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // รูปแบบวันที่
  const formatDate = (date) => {
    return dayjs(date).format('DD/MM/YYYY HH:mm')
  }

  // แสดงความเห็นแต่ละรายการ
  const renderComment = (comment, isReply = false) => {
    return (
      <div
        key={comment.id}
        style={{
          marginBottom: isReply ? 8 : 16,
          padding: isReply ? '8px 12px' : '12px',
          backgroundColor: isReply ? '#fafafa' : '#fff',
          borderRadius: 8,
          border: isReply ? '1px solid #e8e8e8' : '1px solid #f0f0f0'
        }}
      >
        {/* Header ของความเห็น */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar
              src={comment.user_avatar}
              size={isReply ? 20 : 24}
              icon={<UserOutlined />}
            />
            <div>
              <Text strong style={{ fontSize: isReply ? 12 : 14 }}>
                {comment.user_name}
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ClockCircleOutlined style={{ fontSize: 10, color: '#999' }} />
                <Text type="secondary" style={{ fontSize: isReply ? 10 : 11 }}>
                  {formatDate(comment.created_at)}
                </Text>
                {comment.is_internal && (
                  <Tag size="small" color="orange">ภายใน</Tag>
                )}
                {comment.is_resolved && (
                  <Tag size="small" color="green">แก้ไขแล้ว</Tag>
                )}
              </div>
            </div>
          </div>

          {/* ปุ่มจัดการ */}
          <Space>

            <Button
              type="text"
              size="small"
              onClick={() => handleReply(comment)}
              style={{ fontSize: isReply ? 10 : 12 }}
            >
              ตอบกลับ
            </Button>

            {canDeleteComments && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDeleteComment(comment)}
                style={{ fontSize: isReply ? 10 : 12 }}
              />
            )}
          </Space>
        </div>

        {/* เนื้อหาความเห็น */}
        <div style={{ marginBottom: 8 }}>
          <Paragraph
            style={{
              margin: 0,
              fontSize: isReply ? 12 : 13,
              lineHeight: 1.4
            }}
          >
            {comment.comment_text}
          </Paragraph>
        </div>

        {/* ไฟล์แนบ (ถ้ามี) */}
        {attachments.filter(att => att.comment_id === comment.id).length > 0 && (
          <div style={{ marginTop: 8 }}>
            <List
              size="small"
              dataSource={attachments.filter(att => att.comment_id === comment.id)}
              renderItem={attachment => (
                <List.Item style={{ padding: '4px 0' }}>
                  <List.Item.Meta
                    avatar={attachment.is_image ? <PictureOutlined /> : <FileOutlined />}
                    title={
                      <Text style={{ fontSize: isReply ? 10 : 11 }}>
                        {attachment.original_filename}
                      </Text>
                    }
                    description={
                      <Text type="secondary" style={{ fontSize: isReply ? 9 : 10 }}>
                        {formatFileSize(attachment.file_size)}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        )}

        {/* ความเห็นตอบกลับ */}
        {comment.replies && comment.replies.length > 0 && (
          <div style={{ marginTop: 8, marginLeft: 16 }}>
            {comment.replies.map(reply => renderComment(reply, true))}
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      title={
        <div>
          <Text>อภิปราย - {task?.task_title}</Text>
          {task && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              {task.task_description && (
                <Text type="secondary">
                  {task.task_description.substring(0, 100)}
                  {task.task_description.length > 100 && '...'}
                </Text>
              )}
            </div>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      style={{ top: 20 }}
    >
      <div style={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
        {/* รายการความเห็น */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 0',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: 16
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spin /> กำลังโหลดความเห็น...
            </div>
          ) : organizedComments.length > 0 ? (
            organizedComments.map(comment => renderComment(comment))
          ) : (
            <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
              ยังไม่มีความเห็น
            </div>
          )}
        </div>

        {/* ฟอร์มสร้างความเห็น */}
        {canCreateComments && (
          <div>
            {replyTo && (
              <div style={{
                padding: 8,
                backgroundColor: '#e6f7ff',
                borderRadius: 4,
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Text type="secondary">
                  กำลังตอบกลับความเห็น
                </Text>
                <Button
                  type="text"
                  size="small"
                  onClick={handleCancelReply}
                >
                  ยกเลิก
                </Button>
              </div>
            )}

            <Form
              form={form}
              onFinish={handleSubmitComment}
              layout="vertical"
            >
              <Form.Item
                name="comment_text"
                rules={[
                  { required: true, message: 'กรุณาใส่เนื้อหาความเห็น' }
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="พิมพ์ความเห็นของคุณ..."
                  showCount
                  maxLength={2000}
                />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Upload
                    beforeUpload={(file) => {
                      handleUpload(file)
                      return false // ป้องกันการอัปโหลดอัตโนมัติ
                    }}
                    showUploadList={false}
                    accept="image/*,.pdf,.doc,.docx,.txt,.zip,.rar"
                  >
                    <Button
                      icon={<PictureOutlined />}
                      size="small"
                      loading={uploadingFile}
                    >
                      แนบไฟล์
                    </Button>
                  </Upload>
                </div>

                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  htmlType="submit"
                  loading={submittingComment}
                  disabled={uploadingFile}
                >
                  ส่งความเห็น
                </Button>
              </div>
            </Form>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default TaskCommentsModal